import { gql } from "@apollo/client";
import axios from "axios";
import AudioPlayer from "material-ui-audio-player";
import { DropzoneArea } from "material-ui-dropzone";
import { useSnackbar } from "notistack";
import { useState } from "react";

import {
  FormControlLabel,
  FormGroup,
  Switch,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  makeStyles,
  FormHelperText
} from "@material-ui/core";
import Delete from "@material-ui/icons/Delete";

import { useTenant } from "src/components/LoggedInAdminProvider";
import RouteLeavingGuard from "src/components/RouteLeavingGuard";
import CREATE_UPLOAD from "src/graphql/createUpload";
import {
  campaignEditVoicemail,
  campaignEditVoicemailVariables
} from "src/graphql/generated/campaignEditVoicemail";
import {
  createUpload,
  createUploadVariables
} from "src/graphql/generated/createUpload";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import {
  UploadType,
  UploadVisibility
} from "src/graphql/generated/globalTypes";
import useMutation from "src/util/useMutation";

import { useCampaignEditEvents } from "./CampaignEditorsProvider";
import SaveButtons from "./SaveButtons";

const useStyles = makeStyles(theme => ({
  saveButtonsWrapper: {
    marginTop: theme.spacing(3)
  },
  toggleWrapper: {
    marginBottom: theme.spacing(3)
  },
  audioCard: {
    paddingTop: theme.spacing(3),
    minHeight: "150px"
  }
}));

const EDIT_CAMPAIGN_VOICEMAIL = gql`
  mutation campaignEditVoicemail(
    $campaignId: ID!
    $answeringMachineDrop: Boolean!
    $answeringMachineDropUploadToken: String
  ) {
    campaignEditVoicemail(
      id: $campaignId
      voicemail: {
        answeringMachineDrop: $answeringMachineDrop
        answeringMachineDropUploadToken: $answeringMachineDropUploadToken
      }
    ) {
      id
      answering_machine_drop
      voicemailUpload {
        fileName
        publicURL
      }
    }
  }
`;

export default function Voicemail({
  campaign,
  goToNextSection
}: {
  campaign: getCampaign_campaign;
  goToNextSection: () => void;
}): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const tenant = useTenant();

  // Toggle for whether to enable voicemail drop (initialzed to the saved
  // config on the server)
  const [answeringMachineDrop, setAnsweringMachineDrop] = useState(
    campaign.answering_machine_drop || false
  );

  // The File object for a new voicemail recording the user is uploading (if
  // present, this overrides the saved voicemailUpload from the server)
  const [newFile, setNewFile] = useState<{
    file: File;
    objectURL: string;
  } | null>(null);

  // If the campaign already has a voicemail, and the user clicks the delete
  // button to be able to upload a new voicemail, we set this flag to indicate
  // that the existing voicemail is "deleted"
  const [fileCleared, setFileCleared] = useState(false);

  // Whether we are in the process of uploading the voicemail or saving
  // the campaign
  const [isSaving, setIsSaving] = useState(false);

  // The user can hit save EITHER if voicemail drop is enabled and they've picked
  // a voicemail file to upload, or if they're disabling voicemail drop (and it's
  // currently enabled)
  const canSave = answeringMachineDrop
    ? !!newFile
    : !!campaign.answering_machine_drop;
  const isDirty =
    answeringMachineDrop != (campaign.answering_machine_drop || false) ||
    !!newFile ||
    fileCleared;

  const [editCampaignVoicemail] = useMutation<
    campaignEditVoicemail,
    campaignEditVoicemailVariables
  >(EDIT_CAMPAIGN_VOICEMAIL, {
    onErrorSnackbarMessage: "Could not edit voicemail"
    //refetchQueries: ["getCampaign"],
    //awaitRefetchQueries: true
  });

  const [createVoicemailUpload] = useMutation<
    createUpload,
    createUploadVariables
  >(CREATE_UPLOAD, {
    onErrorSnackbarMessage: "Could not access voicemail storage"
  });

  const sendEditEvent = useCampaignEditEvents();

  const handleSave = async ({ goToNext }: { goToNext: boolean }) => {
    if (!isDirty) {
      goToNextSection();
      return;
    }

    setIsSaving(true);

    let uploadToken: null | string = null;
    if (answeringMachineDrop && newFile) {
      // Get an upload URL
      const uploadURL = await createVoicemailUpload({
        variables: {
          campaignId: campaign.id,
          type: UploadType.CAMPAIGN_VOICEMAIL,
          contentType: newFile.file.type,
          fileName: newFile.file.name,
          visibility: UploadVisibility.PUBLIC
        }
      });
      if (uploadURL.errors) {
        setIsSaving(false);
        return;
      }

      // Upload the file
      try {
        await axios.put(
          uploadURL.data!.createUpload.presignedPutURL,
          newFile.file,
          {
            headers: {
              "Content-Type": newFile.file.type
            }
          }
        );
      } catch (e) {
        console.error(e);
        setIsSaving(false);
        enqueueSnackbar("Failed to upload voicemail", { variant: "error" });
        return;
      }

      uploadToken = uploadURL.data?.createUpload.token || null;
    }

    // Update campaign
    const startJobResult = await editCampaignVoicemail({
      variables: {
        campaignId: campaign.id,
        answeringMachineDrop,
        answeringMachineDropUploadToken: uploadToken
      }
    });

    if (startJobResult.errors) {
      setIsSaving(false);
      return;
    }

    sendEditEvent();

    // clear local state; we're just showing the server state now
    setIsSaving(false);
    setNewFile(null);
    setFileCleared(false);
    if (goToNext) {
      goToNextSection();
    } else {
      enqueueSnackbar("Saved voicemail", { variant: "info" });
    }
  };

  function handleDropzoneChange(files: File[]) {
    if (files.length === 0) {
      return;
    }

    const file = files[0];
    setNewFile({ file, objectURL: URL.createObjectURL(file) });
    setFileCleared(false);
  }

  function renderFile(): JSX.Element | null {
    if (!answeringMachineDrop) {
      // answering machine drop disabled
      return null;
    }

    if (fileCleared || (!newFile && !campaign.voicemailUpload)) {
      // Either there's no voicemail file for this campaign, or the user is
      // replacing it with a new one
      return (
        <DropzoneArea
          onChange={handleDropzoneChange}
          dropzoneText="Drop an MP3 or WAV here or click to select a file"
          acceptedFiles={["audio/mpeg", "audio/wav"]}
          showAlerts={false}
          onAlert={(msg, variant) => enqueueSnackbar(msg, { variant })}
        />
      );
    }

    // There's a voicemail file (either already-uploaded, or one that the
    // user has picked)
    let fileName: string;
    let fileURL: string;
    if (newFile) {
      // local file that has been picked but not uploaded
      fileName = newFile.file.name;
      fileURL = newFile.objectURL;
    } else {
      fileName = campaign.voicemailUpload!.fileName;
      fileURL = campaign.voicemailUpload!.publicURL!;
    }

    return (
      <Card>
        <CardHeader
          action={
            <IconButton
              onClick={() => {
                setNewFile(null);
                setFileCleared(true);
              }}
            >
              <Delete />
            </IconButton>
          }
          title={fileName}
        />
        <CardContent className={classes.audioCard}>
          <AudioPlayer src={fileURL} elevation={0} variation="primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <RouteLeavingGuard when={isDirty} />
      <h2>Voicemail</h2>
      <FormGroup className={classes.toggleWrapper}>
        <FormControlLabel
          control={
            <Switch
              checked={answeringMachineDrop}
              onChange={evt => setAnsweringMachineDrop(evt.target.checked)}
              disabled={!tenant?.voicemail_allowed}
              color="primary"
            />
          }
          label="Enable voicemail drop"
        />
        {!tenant?.voicemail_allowed && (
          <FormHelperText>
            Pre-recorded voicemail has been disabled.
          </FormHelperText>
        )}
      </FormGroup>
      <div>{renderFile()}</div>
      <div className={classes.saveButtonsWrapper}>
        <SaveButtons
          handleSave={handleSave}
          isSaving={isSaving}
          isDisabled={isDirty && !canSave}
          variant={isDirty ? "saveAndContinue" : "continueOnly"}
        />
      </div>
    </>
  );
}
